<template>
  <div class="label-crops-panel" :data-label="label_name">
    <loader :loading="loading"></loader>
    <annotate-modal
        v-if="showAnnotateModal && modalAssetId"
        @remove_item="removeGridItem"
        @close_modal="closeAnnotateModal"
        :asset_id="modalAssetId"
    ></annotate-modal>
    <row v-if="topValidatedAnnotations.length > 0" class="label-crops-modal__top-row">
      <col12>
        <div class="label-crops-modal__top-row-title">
          {{$t('myboxes.top_validated_annotations') || 'Top validated annotations'}}
        </div>
        <row>
          <col3
              v-for="image in topValidatedAnnotations" :key="image.bbox_id"
          >
            <grid-item :no_toggle="true"
                       :image="image"
                       :selected="false"
                       class="label-crops-modal__top-item"
                       @remove_item="removeGridItem"
                       @select_item="openAnnotateModal"
                       :validation="image.validation">
            </grid-item>
          </col3>
        </row>
      </col12>
    </row>
    <grid
        ref="grid"
        @reload="load"
        :items="remainingImages"
        :no_toggle="false"
        :show_incorrect_button="showActions"
        v-on:remove_item="removeGridItem"
        @mark_incorrect="markCropIncorrect"
        @update_selection_annotation_ids="updateSelectedAnnotations"
    ></grid>
    <div
        v-if="showActions && remainingImages.length > 0"
        class="label-crops-panel__actions label-crops-panel__actions--bottom mt-3"
        data-testid="crop-validate-selected-bar"
    >
      <v-btn
          data-testid="validate-selected-crops"
          @click="validateSelectedCrops"
          :disabled="loading || selectedAnnotationIds.length === 0"
          color="primary">
        {{$t('myboxes.validate_selected') || 'Validate Selected'}}
      </v-btn>
    </div>
  </div>
</template>

<script>
import Grid from "@/components/Grid";
import GridItem from "@/components/GridItem";
import AnnotateModal from "@/components/AnnotateModal";
import Loader from "@/components/layout/loader";
import Row from "@/components/layout/Row";
import Col12 from "@/components/layout/Col12";
import Col3 from "@/components/layout/Col3";
import { buildCropGridItem, preloadCropItems } from "@/utils/cropTask";
import { blockAnnotateModalOpen, canOpenAnnotateModal } from '@/utils/annotateModalGuard';

const INCORRECT_LABEL = 'Incorrect';

export default {
  name: "LabelCropsPanel",
  components: {Grid, GridItem, AnnotateModal, Loader, Row, Col12, Col3},
  props: {
    label_name: {required: true},
    filters: {required: true},
    total_count: {required: false, default: 0},
    showActions: {type: Boolean, default: true},
    /** Optional preloaded crop grid items (skips initial crop fetches for main grid). */
    preloadedImages: {type: Array, default: null},
  },
  emits: ['loaded', 'validated', 'empty', 'selection-change', 'loading-change'],
  data () {
    return {
      loading: false,
      images: [],
      topValidatedAnnotations: [],
      selectedAnnotationIds: [],
      grid_selected_ids: [],
      showAnnotateModal: false,
      modalAssetId: null,
    }
  },
  computed: {
    remainingImages() {
      let topBboxIds = this.topValidatedAnnotations.map(img => img.bbox_id);
      return this.images.filter(img => !topBboxIds.includes(img.bbox_id));
    }
  },
  watch: {
    label_name() {
      this.load();
    },
    filters: {
      deep: true,
      handler() {
        this.load();
      }
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    openAnnotateModal(assetId) {
      const resolvedAssetId = assetId || null;
      if (!canOpenAnnotateModal()) {
        return;
      }
      if (!resolvedAssetId || this.showAnnotateModal) {
        return;
      }
      this.modalAssetId = resolvedAssetId;
      this.showAnnotateModal = true;
    },
    closeAnnotateModal() {
      blockAnnotateModalOpen();
      this.modalAssetId = null;
      this.showAnnotateModal = false;
    },
    async loadTopValidatedAnnotations() {
      let validatedFilters = [
        {"type": 17, "value": "Yes"}
      ];

      let results = await this.$api.annotation.filter_by_label(validatedFilters, this.label_name);
      let items = results.items || [];

      items.sort((a, b) => {
        const aLiked = a.liked || false;
        const bLiked = b.liked || false;
        if (aLiked === bLiked) return 0;
        return aLiked ? -1 : 1;
      });

      items = items.slice(0, 4);
      this.topValidatedAnnotations = await preloadCropItems(
        items,
        (url, bbox, assetId) => this.$api.asset.get_crop(url, bbox, assetId),
        4,
      );
    },
    async load() {
      this.images = [];
      this.topValidatedAnnotations = [];
      this.selectedAnnotationIds = [];
      this.grid_selected_ids = [];
      this.loading = true
      this.$emit('loading-change', true);

      await this.loadTopValidatedAnnotations();

      let results = await this.$api.annotation.filter_by_label(this.filters, this.label_name);
      let items = results.items || [];

      if (items.length > 40) {
        items = items.slice(0, 40);
      }

      if (items.length === 0) {
        this.loading = false
        this.$emit('loading-change', false);
        this.$emit('empty');
        this.$emit('loaded', { count: 0 });
        this.$emit('selection-change', []);
        return;
      }

      if (Array.isArray(this.preloadedImages) && this.preloadedImages.length > 0) {
        const byBbox = new Map(this.preloadedImages.map((img) => [img.bbox_id, img]));
        const merged = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (byBbox.has(item.bbox_id)) {
            merged.push(byBbox.get(item.bbox_id));
          } else {
            const data = await this.$api.asset.get_crop(item.url, item.bbox, item.asset_id);
            const crops = (data && data.crops) || [];
            for (let j = 0; j < crops.length; j++) {
              merged.push(buildCropGridItem(item, crops[j]));
            }
          }
        }
        this.images = merged;
      } else {
        this.images = await preloadCropItems(
          items,
          (url, bbox, assetId) => this.$api.asset.get_crop(url, bbox, assetId),
          items.length,
        );
      }

      this.loading = false
      this.$emit('loading-change', false);
      this.$emit('loaded', { count: this.images.length });
      this.$nextTick(() => {
        if (this.$refs.grid && this.remainingImages.length > 0) {
          this.$refs.grid.toggleSelectAll(true);
        }
      });
    },
    updateSelectedAnnotations(annotationIds) {
      this.grid_selected_ids = annotationIds;
      this.selectedAnnotationIds = annotationIds;
      this.$emit('selection-change', annotationIds || []);
    },
    async validateSelectedCrops() {
      if (this.selectedAnnotationIds.length === 0) {
        return;
      }
      this.loading = true;
      this.$emit('loading-change', true);
      try {
        await this.$api.box.mark_bboxes_validated(this.selectedAnnotationIds);
        // Crop-task parent advances on `validated`; skip reload to avoid a second advance via `empty`.
        this.$emit('validated');
        if (!this.showActions) {
          await this.load();
        }
      } finally {
        this.loading = false;
        this.$emit('loading-change', false);
      }
    },
    async relabelSelectedAsIncorrect() {
      if (this.selectedAnnotationIds.length === 0) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.annotation.re_label(
          INCORRECT_LABEL,
          JSON.stringify(this.selectedAnnotationIds),
        );
        await this.load();
        this.$emit('validated');
      } catch (error) {
        console.error('Failed to relabel annotations as Incorrect', error);
      } finally {
        this.loading = false;
      }
    },
    async markCropIncorrect(bboxId) {
      if (bboxId == null || bboxId === '') {
        return;
      }
      if (this.$refs.grid && this.$refs.grid.setIncorrectBusyBboxId) {
        this.$refs.grid.setIncorrectBusyBboxId(bboxId);
      }
      try {
        await this.$api.annotation.re_label(
          INCORRECT_LABEL,
          JSON.stringify([bboxId]),
        );
        this.images = this.images.filter((image) => image.bbox_id !== bboxId);
        this.selectedAnnotationIds = this.selectedAnnotationIds.filter((id) => id !== bboxId);
        this.grid_selected_ids = this.grid_selected_ids.filter((id) => id !== bboxId);
        this.$emit('selection-change', this.selectedAnnotationIds);
        if (this.remainingImages.length === 0) {
          this.$emit('empty');
          return;
        }
        // Grid clears selection when `items` change; re-select remaining crops.
        this.$nextTick(() => {
          if (this.$refs.grid && this.remainingImages.length > 0) {
            this.$refs.grid.toggleSelectAll(true);
          }
        });
      } catch (error) {
        console.error('Failed to relabel crop as Incorrect', error);
      } finally {
        if (this.$refs.grid && this.$refs.grid.setIncorrectBusyBboxId) {
          this.$refs.grid.setIncorrectBusyBboxId(null);
        }
      }
    },
    removeGridItem(asset_id) {
      this.images = this.images.filter((image) => image.id !== asset_id);
      this.topValidatedAnnotations = this.topValidatedAnnotations.filter((image) => {
        return image.id !== asset_id
      });
    },
  }
}
</script>

<style scoped>
.label-crops-modal__top-row {
  margin-bottom: 20px;
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
}
.label-crops-modal__top-row-title {
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 10px;
}
.label-crops-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.label-crops-panel__actions--bottom {
  justify-content: flex-end;
}
</style>
